import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { UserMonitorConnectionStatus } from '../../../../shared/models/users/user-monitor-connection-status.model';
import { filter, map, take, tap } from 'rxjs/operators';
import { UsersSelectors } from '../../../../core/users/selectors/users-selectors';
import { User, UserListItem, UserRole } from '../../../../shared/models';
import { AuthSelectors } from '../../../../core/auth/selectors/auth.selectors';
import { BaseAppState } from '../../../../core/store/reducers';
import { ModalsActions } from '../../../../core/modals/actions/modals.actions';
import { UserApiActions } from '../../../../core/users/actions/user-api.actions';
import { ObservableComponent } from 'ngx-esprio-shared';
import { Router } from '@angular/router';
import { USER_UI_PATHS } from '../../../../core/users/definitions/user-ui-navigation.definitions';

@Component({
  selector: 'app-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
})
export class UsersListComponent extends ObservableComponent implements OnInit, AfterViewInit {
  readonly MONITOR_CONNECTION_STATUS = UserMonitorConnectionStatus;
  public loggedInUserRole: UserRole;
  public loggedInUserId: string;

  public dataSource = new MatTableDataSource<UserListItem>([]);
  public displayedColumns: string[] = [
    'name',
    'lastActivity',
    'flow',
    'bmi',
    'weight',
    'lastWeightDate',
    'nutritionIssues',
    'lastSurveyDate',
    'monitorConnectionStatus',
    'remove',
  ];

  @ViewChild(MatSort) sort: MatSort;

  private loggedInUser$ = this.store.select(AuthSelectors.getLoggedInUser);

  constructor(private store: Store<BaseAppState>, private router: Router) {
    super();

    this.dataSource.filterPredicate = (data: UserListItem, filter: string) => {
      const search = filter.trim().toLowerCase();
      const lastFirst = [data.lastName, data.firstName].filter(Boolean).join(', ').toLowerCase();

      return (
        (data.firstName?.toLowerCase().includes(search) ?? false) ||
        (data.lastName?.toLowerCase().includes(search) ?? false) ||
        (data.email?.toLowerCase().includes(search) ?? false) ||
        lastFirst.includes(search)
      );
    };

    this.dataSource.sortingDataAccessor = (item: UserListItem, property: string) => {
      switch (property) {
        case 'name':
          return item.lastName?.toLowerCase() || '';
        case 'flow':
          return this.flowSortValue(item.flow);
        case 'bmi':
          return item.scoreDetails?.bmi ?? 0;
        case 'weight':
          return item.scoreDetails?.weightChangePercentage ?? 0;
        case 'nutritionIssues':
          return item.scoreDetails?.strasserScore ?? -1;
        case 'lastWeightDate':
          return item.lastWeightDate ? new Date(item.lastWeightDate).getTime() : 0;
        case 'lastSurveyDate':
          return item.lastSurveyDate ? new Date(item.lastSurveyDate).getTime() : 0;
        case 'lastActivity': {
          const activity = this.lastActivityOf(item);
          return activity ? activity.getTime() : 0;
        }
        default:
          return (item as any)[property];
      }
    };

  }

  /** The most recent of the last weight / last survey dates ("last seen"). */
  public lastActivityOf(user: UserListItem): Date | null {
    const dates = [user.lastWeightDate, user.lastSurveyDate]
      .filter((d): d is string => !!d)
      .map((d) => new Date(d));
    // "Letzte Aktivität" is never empty: with no weight/survey entry yet, the
    // account creation date is the user's first data entry (ticket 27757).
    if (!dates.length) {
      return user.createdAt ? new Date(user.createdAt) : null;
    }
    return dates.reduce((latest, d) => (d > latest ? d : latest));
  }

  /** Absolute date, e.g. "05.08.2026". */
  public formatDate(date: Date | string | null | undefined): string {
    if (!date) {
      return '–';
    }
    const d = new Date(date);
    const day = `${d.getDate()}`.padStart(2, '0');
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    return `${day}.${month}.${d.getFullYear()}`;
  }

  /** Relative date, e.g. "heute" / "gestern" / "vor 3 Tagen". */
  public relativeDate(date: Date | string | null | undefined): string {
    if (!date) {
      return '';
    }
    const then = new Date(date);
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((startOfDay(new Date()) - startOfDay(then)) / 86400000);
    if (days <= 0) {
      return 'heute';
    }
    if (days === 1) {
      return 'gestern';
    }
    return `vor ${days} Tagen`;
  }

  ngOnInit(): void {
    this.loggedInUser$
      .pipe(
        filter((user: User) => !!user),
        take(1),
        tap((user) => {
          this.loggedInUserId = user.id;
          this.loggedInUserRole = user.role;

          if (user.role === UserRole.SuperAdvisor) {
            const removeIndex = this.displayedColumns.indexOf('remove');
            this.displayedColumns.splice(removeIndex, 0, 'subadvisor');
          }

          this.store.dispatch(UserApiActions.getMonitoredUsers({ userId: user.id }));
        })
      )
      .subscribe();

    this.store
      .select(UsersSelectors.getUsers)
      .pipe(
        map((users: UserListItem[]) =>
          users.filter((user) => user.role !== UserRole.RelativeUserAccount)
        )
      )
      .subscribe((monitoredUsers) => {
        this.dataSource.data = monitoredUsers || [];
      });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    setTimeout(() => {
      // Default: most recently active patients first (ticket 27757).
      this.sort.active = 'lastActivity';
      this.sort.direction = 'desc';
      this.dataSource.sort = this.sort;
    });

    this.store.dispatch(UserApiActions.getMonitoredUsersInvitations());
  }

  public applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  public handleOpenUserDetails(user: UserListItem) {
    if (user.monitorConnectionStatus === UserMonitorConnectionStatus.Confirmed) {
      this.router.navigate([USER_UI_PATHS.root.users, user.id]);
    }
  }

  public getStrasserScoreTranslationKey(score: number): string {
    if (score >= 2) return 'monitoring.profile_data.strasser_score.strong';
    if (score === 1) return 'monitoring.profile_data.strasser_score.medium';
    return 'monitoring.profile_data.strasser_score.weak';
  }

  public handleRemoveMonitoredUsers(userIds: string[]) {
    this.store.dispatch(ModalsActions.openConfirmRemoveMonitoredUsersModal({ userIds }));
  }

  public getAllowedMonitoringUsersText(user: UserListItem): string {
    if (user.allowedMonitoringUsers?.length) {
      return user.allowedMonitoringUsers.map((u) => `${u.firstName} ${u.lastName}`).join(', ');
    }
    return '-';
  }

  public canRemoveUser(user: UserListItem): boolean {
    if (user.allowedMonitoringUserIds) {
      return user.allowedMonitoringUserIds?.includes(this.loggedInUserId);
    }
    return false;
  }

  public handleOpenInvitePatientModal() {
    this.store.dispatch(ModalsActions.openInviteMonitoredUserModal());
  }

  public flowSortValue(flow: string | undefined) {
    if (!flow) return 999;
    switch (flow.toLowerCase()) {
      case 'red': return 1;
      case 'yellow': return 2;
      case 'green': return 3;
      default: return 999;
    }
  }

}
